import { useCallback, useEffect, useMemo, useState } from 'react';

import { carouselResponsive, routes } from '../../config/config';
import { imgSrcParser } from '../../utils/imgSrcParser';
import { Card } from '../Card';
import { FarmCard } from '../Card/FarmCard';
import { Carousel } from '../Carousel/Carousel';
import { GoogleMap, GoogleMapFeature, GoogleMapPopup } from '../GoogleMap';
import { ClusterCarousel, PopupContainer } from './style';
import { BannerImageDefaults } from '../UploadImage/fallBack/FallBackImage';
import { MapModal } from '../Modal/MapModal/MapModal';
import { FarmData } from 'types/FarmData';
import { ProductData } from 'types/ProductData';

interface MapProps {
  data: any[];
  dataType: 'farms' | 'products';
}

interface MapFeature extends GoogleMapFeature {
  farm_id?: number;
  product_id?: number;
}

const fetchByIds = async (path: string, ids: number[]) => {
  if (ids.length === 0) return [];
  const query = ids.map((id) => `ids[]=${id}`).join('&');
  const response = await fetch(`${routes.api.baseUrl}${path}?${query}`);
  if (!response.ok) return [];
  const json = await response.json();
  return json?.data ?? json ?? [];
};

const fetchFarmsByInfrastructureIds = (ids: number[]) =>
  fetchByIds('/api/v2/farms/by-infrastructure-ids', ids);

const fetchProductsByIds = (ids: number[]) =>
  fetchByIds('/api/v2/products/by-ids', ids);

const resolveFirstImageUrl = (
  kind: MapProps['dataType'],
  entityData: any
): string | undefined => {
  if (!entityData) return undefined;

  let found: unknown;
  if (kind === 'products') {
    const first = Array.isArray(entityData?.images)
      ? entityData.images[0]
      : null;
    found = [
      first?.image_small,
      first?.small,
      first?.url,
      first?.image,
      entityData?.image_small,
    ].find((u) => typeof u === 'string' && u.trim());
  } else {
    found = entityData?.logo || entityData?.image;
  }

  return imgSrcParser(found as string | undefined) || undefined;
};

const MapComponent = ({ data, dataType }: MapProps) => {
  const [clusterItems, setClusterItems] = useState<(FarmData | ProductData)[]>(
    []
  );
  const [clusterOpen, setClusterOpen] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState<GoogleMapPopup | null>(
    null
  );

  const features = useMemo<MapFeature[]>(() => {
    return (data ?? [])
      .map((item) => {
        const p = item.farmdata || item;

        const productId =
          item.product?.id ??
          item.product_id ??
          p?.product_id ??
          item?.product?.product_id ??
          undefined;

        const idNum = Number(p?.id);
        if (!Number.isFinite(idNum)) return null;

        // GeoJSON position: [lng, lat] (WGS84)
        const coords =
          p?.coordinates?.coordinates ?? item?.coordinates?.coordinates;
        const [lng, lat] = Array.isArray(coords) ? coords : [];
        if (typeof lat !== 'number' || typeof lng !== 'number') return null;

        return {
          id: idNum,
          position: { lat, lng },
          farm_id: p?.farm_id ?? item?.farm_id,
          product_id: productId ? Number(productId) : undefined,
        };
      })
      .filter(Boolean) as MapFeature[];
  }, [data]);

  const featureById = useMemo(
    () => new Map(features.map((f) => [f.id, f])),
    [features]
  );

  useEffect(() => {
    setSelectedPopup(null);
  }, [data]);

  const buildPopupContent = useCallback(
    (entityId: number | null, entityData: any) => {
      const imageUrl = resolveFirstImageUrl(dataType, entityData);
      const fallbackRel =
        dataType === 'products'
          ? BannerImageDefaults.ProductCard
          : BannerImageDefaults.FarmCard;
      const srcOrFallback =
        imageUrl || `${routes.api.baseUrl}/client_assets${fallbackRel}`;

      return (
        <PopupContainer
          data-entity-type={dataType}
          data-entity-id={entityId ?? undefined}
          data-image-fallback={imageUrl ? undefined : 'true'}
        >
          {dataType === 'farms' ? (
            <FarmCard
              farm={{ ...(entityData || {}), logo: srcOrFallback }}
              isFarmMap
              plainLogoUrl={srcOrFallback}
            />
          ) : (
            <Card
              plainImageUrl={srcOrFallback}
              imageUrl={srcOrFallback}
              product={entityData}
            />
          )}
        </PopupContainer>
      );
    },
    [dataType]
  );

  const handleFeatureClick = useCallback(
    async (featureId: number) => {
      const feature = featureById.get(featureId);
      if (!feature) return;

      const isFarms = dataType === 'farms';
      const entityId = (isFarms ? feature.farm_id : feature.product_id) ?? null;
      const entityData = isFarms
        ? ((await fetchFarmsByInfrastructureIds([featureId]))[0] ?? null)
        : entityId != null
          ? ((await fetchProductsByIds([entityId]))[0] ?? null)
          : null;

      setSelectedPopup({
        featureId,
        content: buildPopupContent(entityId, entityData),
      });
    },
    [dataType, featureById, buildPopupContent]
  );

  const handleClusterClick = useCallback(
    async (featureIds: number[]) => {
      if (featureIds.length <= 1) return;

      let items: unknown;
      if (dataType === 'farms') {
        items = await fetchFarmsByInfrastructureIds(featureIds);
      } else {
        const productIds = featureIds
          .map((id) => featureById.get(id)?.product_id ?? id)
          .filter((id): id is number => id != null);
        items = await fetchProductsByIds(productIds);
      }

      setClusterItems(Array.isArray(items) ? items : []);
      setClusterOpen(true);
    },
    [dataType, featureById]
  );

  const handlePopupClose = useCallback(() => setSelectedPopup(null), []);

  return (
    <>
      <GoogleMap
        features={features}
        popup={selectedPopup}
        onFeatureClick={handleFeatureClick}
        onClusterClick={handleClusterClick}
        onPopupClose={handlePopupClose}
      />
      <MapModal
        dataType={dataType}
        count={clusterItems.length}
        open={clusterOpen}
        onClose={() => setClusterOpen(false)}
      >
        <ClusterCarousel>
          <Carousel
            responsive={carouselResponsive}
            data={clusterItems as ProductData[] | FarmData[]}
            dataType={dataType}
            dots
            draggable
            slidesPerRow={1}
          />
        </ClusterCarousel>
      </MapModal>
    </>
  );
};

export default MapComponent;
